// Schema.org JSON-LD generators per page type. Anything emitted here gets
// dropped into a <script type="application/ld+json"> in the document head
// by the SeoHead component, so AI scrapers and Google parse the page's
// semantic intent without rendering the SPA.
import type { EventRecord, Category } from './events';
import { totalCases } from './events';
import { config } from './config';
import { formatCounts } from './counts';

const SITE_URL = `https://${config.app.domain}`;
// TODO (per app): replace with your organization's name + URL.
const ORG_NAME = config.app.name;
const ORG_URL = SITE_URL;

// ---------------------------------------------------------------------------
// shared base nodes
// ---------------------------------------------------------------------------

export function organizationNode() {
  return {
    '@type': 'Organization',
    '@id': `${SITE_URL}#org`,
    name: ORG_NAME,
    url: ORG_URL,
  } as const;
}

export function websiteNode() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}#site`,
    name: config.app.domain,
    url: SITE_URL,
    description: config.app.description,
    inLanguage: 'en',
    publisher: { '@id': `${SITE_URL}#org` },
    license: 'https://creativecommons.org/licenses/by/4.0/',
  } as const;
}

// ---------------------------------------------------------------------------
// page-specific generators
// ---------------------------------------------------------------------------

/** Root map page — anchors a Dataset claim so AI/researchers can cite the data. */
export function mapPageJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      websiteNode(),
      {
        '@type': 'Dataset',
        '@id': `${SITE_URL}#dataset`,
        name: `${config.app.name} dataset`,
        description: config.app.description,
        url: SITE_URL,
        keywords: ['event', 'map', 'dataset'],
        license: 'https://creativecommons.org/licenses/by/4.0/',
        isAccessibleForFree: true,
        creator: { '@id': `${SITE_URL}#org` },
        publisher: { '@id': `${SITE_URL}#org` },
        spatialCoverage: { '@type': 'Place', name: 'Global' },
        // Bulk download is intentionally not offered. Each event's
        // citations link directly to its primary public-health source
        // (WHO / CDC / national MoH), which remains the canonical
        // redistributable artifact.
        distribution: [],
      },
    ],
  };
}

/** Per-event page — Article-as-MedicalIndication. AI cites these by URL.
 *  Also emits a ClaimReview node so Google + LLM crawlers see this page as
 *  a verified-claim source. The reviewRating maps our internal confidence
 *  enum onto Google's 1-5 numeric scale. */
export function eventJsonLd(o: EventRecord) {
  const total = totalCases(o);
  const counts = formatCounts(o.counts as unknown as Record<string, number | undefined>, config);
  const deathsCount = counts.find((c) => c.id === 'deaths')?.value ?? 0;
  // Map our 3-tier confidence onto Google's ClaimReview rating scale.
  // verified=5, preliminary=3, unverified=2. The textual rating is what
  // surfaces in rich results; the numeric value drives Google's ranking.
  const ratingMap: Record<string, { value: number; text: string; explanation: string }> = {
    verified: {
      value: 5, text: 'Verified',
      explanation: 'All numeric claims verified against verbatim quotes from tier-1 primary sources (WHO, CDC, ECDC, PAHO, or national health ministries).',
    },
    preliminary: {
      value: 3, text: 'Preliminary',
      explanation: 'Numeric claims verified against tier-2 sources (ProMED-mail, CIDRAP, peer-reviewed literature). Pending tier-1 confirmation.',
    },
    unverified: {
      value: 2, text: 'Unverified',
      explanation: 'Reported via tier-3 news sources only. Awaiting authoritative confirmation.',
    },
  };
  const rating = ratingMap[o.confidence] ?? ratingMap.preliminary;
  // Build claim text by iterating over the config-driven counts fields so
  // apps with different metrics get their own structured claim string.
  const countsFragment = counts.map((c) => `${c.value} ${c.label.toLowerCase()}`).join(', ');
  const claimText =
    `${o.location.name}: ${countsFragment} ` +
    `(${o.category}, ${o.secondary}, started ${o.start_date}).`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      {
        '@type': 'Article',
        '@id': `${SITE_URL}/event/${o.id}#article`,
        headline: o.location.name,
        url: `${SITE_URL}/event/${o.id}`,
        datePublished: o.start_date,
        dateModified: o.updated_at ?? o.ingestion.last_updated,
        description: o.summary.slice(0, 280),
        articleSection: o.category,
        about: {
          '@type': 'Thing',
          name: o.category,
        },
        publisher: { '@id': `${SITE_URL}#org` },
        author: { '@id': `${SITE_URL}#org` },
        license: 'https://creativecommons.org/licenses/by/4.0/',
        citation: o.sources.map(s => ({
          '@type': 'CreativeWork',
          name: s.title,
          publisher: s.publisher,
          url: s.url,
          dateAccessed: s.accessed,
        })),
        mentions: [
          {
            '@type': 'Place',
            name: o.location.name,
            geo: {
              '@type': 'GeoCoordinates',
              latitude: o.location.lat,
              longitude: o.location.lng,
            },
            address: { '@type': 'PostalAddress', addressCountry: o.country_iso2 },
          },
        ],
        // Surface the case totals as structured numbers — AI will cite these.
        keywords: [
          `${total} cases`,
          `${deathsCount} deaths`,
          o.category,
          o.country_iso2,
        ].join(', '),
      },
      // ClaimReview — eligible for Google rich-result fact-check snippet
      // and prioritised by LLM crawlers as a structured-claim source.
      {
        '@type': 'ClaimReview',
        '@id': `${SITE_URL}/event/${o.id}#claimreview`,
        url: `${SITE_URL}/event/${o.id}`,
        datePublished: o.start_date,
        author: { '@id': `${SITE_URL}#org` },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: rating.value,
          bestRating: 5,
          worstRating: 1,
          alternateName: rating.text,
          ratingExplanation: rating.explanation,
        },
        claimReviewed: claimText,
        itemReviewed: {
          '@type': 'Claim',
          appearance: o.sources.map(s => ({
            '@type': 'CreativeWork',
            url: s.url,
            name: s.title,
            publisher: s.publisher,
          })),
        },
      },
    ],
  };
}

/**
 * Per-category page — generic WebPage JSON-LD. Apps that want richer
 * structured data (e.g. MedicalCondition for a health-themed app) should
 * replace this with a domain-specific generator.
 */
export function categoryJsonLd(opts: { id: string; label: string; description?: string }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/category/${opts.id}#page`,
        name: opts.label,
        url: `${SITE_URL}/category/${opts.id}`,
        description: (opts.description ?? '').slice(0, 500),
        publisher: { '@id': `${SITE_URL}#org` },
      },
    ],
  };
}

/** FAQ page schema — drives rich snippets in Google search results. */
export function faqJsonLd(qas: Array<{ q: string; a: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qas.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

/** Generic page (about / disclaimer) — anchors organization + license. */
export function pageJsonLd(opts: { title: string; description: string; url: string }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      {
        '@type': 'WebPage',
        name: opts.title,
        description: opts.description,
        url: `${SITE_URL}${opts.url}`,
        publisher: { '@id': `${SITE_URL}#org` },
        license: 'https://creativecommons.org/licenses/by/4.0/',
      },
    ],
  };
}

// helper used by callers
export const SEO_SITE_URL = SITE_URL;
export const SEO_CATEGORY_INFO_KEYS = (xs: Category[]): Category[] => xs;
